<template>
  <div ref="root">
    <div
      @click="showDropdown"
      class="tw:relative tw:cursor-pointer tw:border tw:rounded-lg tw:flex box-size-44 tw:items-center tw:px-3 tw:py-1 tw:pr-8 tw:h-[41px]"
      :class="show ? 'tw:border-blue-800' : 'tw:border-gray-500'"
    >
      <span class="tw:truncate tw:absolute tw:top-[3px] tw:text-xs tw:font-semibold">{{ label }}</span>
      <span class="tw:truncate tw:absolute tw:bottom-[3px] tw:text-sm tw:max-w-[120px]">
        {{ selectedLabels }}
      </span>
      <ChevronRight class="tw:absolute tw:size-6" :class="show ? 'arrow-dropdown-open' : 'arrow-dropdown'" />
    </div>

    <div class="tw:relative tw:top-[5px] tw:z-20" v-if="show">
      <div class="tw:absolute tw:min-h-[50px] tw:right-0 tw:left-0 tw:bg-white tw:rounded dropdown-card">
        <ul class="tw:max-h-[180px] tw:overflow-auto">
          <template v-for="(item, index) in filteredOptions" :key="`item-${index}`">
            <li
              v-if="!item.group"
              @click="onSelect(item)"
              :class="getOptionClass(isSelected(item), index === 0)"
              class="tw:flex tw:items-center tw:justify-between tw:p-4 tw:hover:bg-blue-100 tw:hover:text-blue-800 tw:hover:cursor-pointer"
            >
              <span class="tw:flex tw:flex-row tw:items-center tw:text-sm tw:gap-2">
                <input type="checkbox" :checked="isSelected(item)" @click.stop class="tw:w-4 tw:h-4" />
                {{ $t(item.label) }}
              </span>
            </li>

            <template v-else>
              <li class="tw:px-4 tw:py-2 tw:text-sm tw:font-bold tw:text-black tw:bg-gray-200">
                {{ $t(item.group.label) }}
              </li>

              <li
                v-for="(option, optIndex) in item.options"
                :key="`group-${index}-opt-${optIndex}`"
                @click="onSelect(option)"
                :class="getGroupOptionClass(isSelected(option), optIndex, item.options.length)"
                class="tw:flex tw:items-center tw:justify-between tw:p-4 tw:hover:bg-blue-100 tw:hover:text-blue-800 tw:hover:cursor-pointer"
              >
                <span class="tw:flex tw:flex-row tw:items-center tw:text-sm tw:gap-2">
                  <input type="checkbox" :checked="isSelected(option)" @click.stop class="tw:w-4 tw:h-4" />
                  {{ $t(option.label) }}
                </span>
              </li>
            </template>
          </template>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onDeactivated, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import ChevronRight from '@/components/icons/ChevronRight.svg?component'

const props = defineProps({
  options: Array,
  name: String,
  value: Array,
  label: String,
})
const { t } = useI18n()
const searchQuery = ref('')
const root = ref(null)
const show = ref(false)
const filteredOptions = ref([])

watch(
  () => props.options,
  newOptions => {
    filteredOptions.value = newOptions
  },
  { immediate: true, deep: true }
)

const isSelected = item => {
  return props.value.filter(option => option.id === item.id)?.length > 0
}

// Get all selectable options (flattened)
const allSelectableOptions = computed(() => {
  const options = []
  props.options.forEach(item => {
    if (!item.group) {
      options.push(item)
    } else {
      options.push(...item.options)
    }
  })
  return options
})

const selectedLabels = computed(() => {
  if (!props.value.length) return ''

  return props.value
    .map(item => {
      const option = allSelectableOptions.value.find(opt => opt.id === item.id)
      return option ? t(option.label) : ''
    })
    .filter(Boolean)
    .join(', ')
})

const showDropdown = () => {
  show.value = !show.value
}

const emit = defineEmits(['select'])

const onSelect = option => {
  emit('select', option)
}

const getOptionClass = (selected, isFirst) => {
  const classes = []
  if (isFirst) {
    classes.push('tw:rounded-t')
  }
  if (selected) {
    classes.push('tw:text-blue-700 tw:bg-blue-100')
  }
  return classes.join(' ')
}

const getGroupOptionClass = (selected, position, groupLength) => {
  const classes = []
  if (position === groupLength - 1) {
    classes.push('tw:border-b tw:border-gray-200')
  }
  if (selected) {
    classes.push('tw:text-blue-700 tw:bg-blue-100')
  }
  return classes.join(' ')
}

watch(searchQuery, newSearchQuery => {
  const q = newSearchQuery.trim().toLowerCase()

  if (q.length === 0) {
    filteredOptions.value = props.options
    return
  }

  const filtered = []

  props.options.forEach(item => {
    if (!item.group) {
      // Single option
      const key = item.label ?? ''
      const translated = t(key)
      const haystack = (translated && translated !== key ? translated : key).toLowerCase()
      if (haystack.includes(q)) {
        filtered.push(item)
      }
    } else {
      // Group
      const filteredGroupOptions = item.options.filter(option => {
        const key = option.label ?? ''
        const translated = t(key)
        const haystack = (translated && translated !== key ? translated : key).toLowerCase()
        return haystack.includes(q)
      })

      if (filteredGroupOptions.length > 0) {
        filtered.push({
          ...item,
          options: filteredGroupOptions,
        })
      }
    }
  })

  filteredOptions.value = filtered
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onDeactivated(() => {
  document.removeEventListener('click', handleClickOutside)
})

const handleClickOutside = evt => {
  if (root?.value !== null && !root?.value?.contains(evt.target)) {
    show.value = false
  }
}
</script>

<style scoped>
.arrow-dropdown {
  top: 50%;
  transform: translateY(-45%) rotate(90deg);
  right: 6px;
  pointer-events: none;
}
.arrow-dropdown-open {
  top: 50%;
  transform: translateY(-45%) rotate(270deg);
  right: 6px;
  pointer-events: none;
}
.dropdown-card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
}
</style>
